import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import { Button } from "@/components/ui/button";
import { Keyboard as KbIcon, X, Globe, Hash } from "lucide-react";

/**
 * Touch-friendly on-screen keyboard that types into the currently
 * focused <input>/<textarea>. Supports three layouts:
 *
 *   • el — Greek (default)
 *   • en — English
 *   • num — Numeric only (when the focused input has inputmode="numeric"
 *           or type="number" or class "font-mono", we auto-switch here)
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <VirtualKeyboard open={open} onOpenChange={setOpen} />
 *   <Button onClick={() => setOpen(true)}><KeyboardIcon/></Button>
 *
 * The keyboard mounts a fixed panel at the bottom of the viewport.
 * Tapping a key writes to `document.activeElement` using the native
 * input setter so React controlled components are notified properly.
 */

// react-simple-keyboard layouts ----------------------------------------------
const LAYOUT_EL = {
  default: [
    "; ς ε ρ τ υ θ ι ο π {bksp}",
    "α σ δ φ γ η ξ κ λ ΄ {enter}",
    "{shift} ζ χ ψ ω β ν μ , . {shift}",
    "{lang} {numpad} {space}",
  ],
  shift: [
    ": Σ Ε Ρ Τ Υ Θ Ι Ο Π {bksp}",
    "Α Σ Δ Φ Γ Η Ξ Κ Λ ¨ {enter}",
    "{shift} Ζ Χ Ψ Ω Β Ν Μ < > {shift}",
    "{lang} {numpad} {space}",
  ],
};
const LAYOUT_EN = {
  default: [
    "q w e r t y u i o p {bksp}",
    "a s d f g h j k l ' {enter}",
    "{shift} z x c v b n m , . {shift}",
    "{lang} {numpad} {space}",
  ],
  shift: [
    "Q W E R T Y U I O P {bksp}",
    'A S D F G H J K L " {enter}',
    "{shift} Z X C V B N M < > {shift}",
    "{lang} {numpad} {space}",
  ],
};
const LAYOUT_NUM = {
  default: [
    "1 2 3 {bksp}",
    "4 5 6 {enter}",
    "7 8 9 {clear}",
    "{lang} 0 . {space}",
  ],
};

const BUTTON_DISPLAY_BASE = {
  "{bksp}": "⌫",
  "{enter}": "↵",
  "{shift}": "⇧",
  "{space}": "␣  Διάστημα",
  "{clear}": "AC",
};

function setNativeValue(el, value) {
  // Bypass React's value cache so controlled <Input> components see the
  // change and re-render their state.
  const proto = el instanceof window.HTMLTextAreaElement ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value").set;
  setter.call(el, value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

export default function VirtualKeyboard({ open, onOpenChange }) {
  const [layout, setLayout] = useState("el");
  const [shift, setShift] = useState(false);
  const targetRef = useRef(null);
  // Track the most recently focused input so taps still work after the
  // user clicks the keyboard (which steals focus back to a button).
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const el = e.target;
      if (!el) return;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") {
        targetRef.current = el;
        // Auto-switch to numeric keypad when the input is clearly
        // numeric-only (phone, vat, etc.).
        const inputMode = el.inputMode || el.getAttribute("inputmode") || "";
        const type = el.type || "";
        const isMono = (el.className || "").includes("font-mono");
        if (type === "number" || inputMode === "numeric" || (isMono && /(phone|vat|code|tel)/i.test(el.name || el.placeholder || ""))) {
          setLayout("num");
        }
      }
    };
    document.addEventListener("focusin", handler, true);
    return () => document.removeEventListener("focusin", handler, true);
  }, [open]);

  const writeChar = useCallback((ch) => {
    const el = targetRef.current;
    if (!el || !document.body.contains(el)) return;
    // Preserve focus on the input.
    if (document.activeElement !== el) el.focus({ preventScroll: true });
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const newVal = el.value.slice(0, start) + ch + el.value.slice(end);
    const maxLen = el.maxLength;
    if (maxLen > 0 && newVal.length > maxLen) return;
    setNativeValue(el, newVal);
    requestAnimationFrame(() => {
      try {
        el.setSelectionRange(start + ch.length, start + ch.length);
      } catch (_e) { /* selection not supported on some inputs */ }
    });
  }, []);

  const backspace = useCallback(() => {
    const el = targetRef.current;
    if (!el || !document.body.contains(el)) return;
    if (document.activeElement !== el) el.focus({ preventScroll: true });
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    let newVal, newCaret;
    if (start === end && start > 0) {
      newVal = el.value.slice(0, start - 1) + el.value.slice(end);
      newCaret = start - 1;
    } else {
      newVal = el.value.slice(0, start) + el.value.slice(end);
      newCaret = start;
    }
    setNativeValue(el, newVal);
    requestAnimationFrame(() => {
      try { el.setSelectionRange(newCaret, newCaret); } catch (_e) { /* noop */ }
    });
  }, []);

  const clearAll = useCallback(() => {
    const el = targetRef.current;
    if (!el || !document.body.contains(el)) return;
    setNativeValue(el, "");
  }, []);

  const handleKeyPress = (btn) => {
    if (btn === "{lang}") {
      setLayout((cur) => (cur === "el" ? "en" : cur === "en" ? "num" : "el"));
      return;
    }
    if (btn === "{numpad}") {
      setLayout("num");
      return;
    }
    if (btn === "{shift}") {
      setShift((s) => !s);
      return;
    }
    if (btn === "{bksp}") {
      backspace();
      return;
    }
    if (btn === "{enter}") {
      writeChar("\n");
      return;
    }
    if (btn === "{space}") {
      writeChar(" ");
      return;
    }
    if (btn === "{clear}") {
      clearAll();
      return;
    }
    writeChar(btn);
  };

  if (!open) return null;

  const currentLayout = layout === "el" ? LAYOUT_EL : layout === "en" ? LAYOUT_EN : LAYOUT_NUM;
  const langLabel = layout === "el" ? "ΕΛ" : layout === "en" ? "EN" : "123";
  const display = { ...BUTTON_DISPLAY_BASE, "{lang}": langLabel, "{numpad}": "123" };

  // Portal at document.body — Radix modal Dialogs apply pointer-events:none
  // and aria-hidden=true to every sibling subtree at body level when they
  // open. If the keyboard lives inside Layout (a Dialog sibling), it inherits
  // pointer-events:none and every key tap passes through to the dialog
  // overlay, which Radix interprets as an outside-click and dismisses the
  // dialog. Rendering as a direct body child sidesteps that contract.
  return createPortal(
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] bg-slate-900 text-white border-t-4 border-emerald-500 shadow-2xl pointer-events-auto"
      data-testid="virtual-keyboard"
      onMouseDown={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        // Prevent Radix dialogs (and any Dismissable layer) from treating
        // keyboard taps as outside-clicks. Without this, every key press
        // would dismiss any currently open dialog because the keyboard
        // panel is a body-level sibling of the dialog content.
        e.preventDefault();
      }}
    >
      <div className="flex items-center justify-between px-4 py-2 bg-slate-800">
        <div className="flex items-center gap-3 text-sm font-bold">
          <KbIcon size={16} /> Πληκτρολόγιο
          <span className="px-2 py-0.5 rounded bg-emerald-600 text-xs font-mono">
            {langLabel}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setLayout("el")}
            className={`text-white ${layout === "el" ? "bg-emerald-600" : ""}`}
            data-testid="vk-lang-el"
          >
            <Globe size={14} className="mr-1" /> ΕΛ
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setLayout("en")}
            className={`text-white ${layout === "en" ? "bg-emerald-600" : ""}`}
            data-testid="vk-lang-en"
          >
            EN
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setLayout("num")}
            className={`text-white ${layout === "num" ? "bg-emerald-600" : ""}`}
            data-testid="vk-lang-num"
          >
            <Hash size={14} className="mr-1" /> 123
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-red-400 hover:text-red-100 hover:bg-red-600 font-bold text-xs tracking-wider px-2"
            data-testid="vk-close"
          >
            ΕΞΟΔΟΣ <X size={14} className="ml-1" />
          </Button>
        </div>
      </div>
      <div className="p-2 bg-slate-900 vk-touch">
        <Keyboard
          layout={currentLayout}
          layoutName={shift ? "shift" : "default"}
          display={display}
          onKeyPress={handleKeyPress}
          theme="hg-theme-default hg-layout-default vk-dark"
          buttonTheme={[
            { class: "vk-special", buttons: "{bksp} {enter} {shift} {space} {lang} {numpad} {clear}" },
          ]}
          physicalKeyboardHighlight={false}
          preventMouseDownDefault={true}
          mergeDisplay={true}
        />
      </div>
    </div>,
    document.body
  );
}
